import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    Inject,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectionList } from '@angular/material/list';
import { MatRadioButton } from '@angular/material/radio';
import { FormControl } from '@ngneat/reactive-forms';
import { go } from 'fuzzysort';
import { combineLatest, Observable } from 'rxjs';
import { debounceTime, map, shareReplay } from 'rxjs/operators';
import { ArticleCrm } from 'src/app/classes/article-crm.class';
import { Article } from 'src/app/classes/article.class';
import { MaterialType } from 'src/app/classes/material-type.class';
import { Material } from 'src/app/classes/material.class';
import { RawMaterial } from 'src/app/classes/raw-material.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { ArticlesService } from 'src/app/services/articles.service';
import {
    flattenSelectedOptionals,
    GroupedOptional,
    groupOptionalsByRelatedType,
    isOptionalSelectionValid as checkOptionalSelectionValid,
    mergePlanningMaterialPivots,
    MaterialService,
} from 'src/app/services/material/material.service';
import { PlanningService } from 'src/app/services/planning/planning.service';

type ColoredMaterial = Material & {
    material_type?: MaterialType;
    color: string;
    color_name: string;
    ral: string;
    partOfPlanning?: boolean;
    planning_material_id?: number;
};
type OptionalMaterial = ColoredMaterial &
    Pick<RawMaterial, 'consumption' | 'consumption_type'> & { type_name: string; optional?: boolean };

export enum STEPS {
    LOCATION = 'LOCATION',
    ARTICLE = 'ARTICLE',
    MATERIAL = 'MATERIAL',
    OPTIONAL = 'OPTIONAL',
    AMOUNT = 'AMOUNT',
}

@Component({
    selector: 'app-add-material-dialog',
    templateUrl: './add-material-dialog.component.html',
    styleUrls: ['./add-material-dialog.component.scss'],
    standalone: false,
})
export class AddMaterialDialogComponent extends CodaltComponent implements AfterViewInit {
    fcLocation = new FormControl<string>();
    fcArticleSearch = new FormControl<string>();
    fcCrmSearch = new FormControl<string>();
    fcMaterialSearch = new FormControl<string>();
    fcAmount = new FormControl<number>();
    articles: { group: string; items: { id: number; name: string }[] }[] = [];
    matchedGroups: { group: string; items: { id: number; name: string }[] }[] = [];
    selectedGroupName: string | null = null;
    matchedGroupArticles: { id: number; name: string }[] = [];
    pickerMode: 'markering' | 'bestekpost' = 'markering';
    crmArticles: ArticleCrm[] = [];
    matchedCrmArticles: ArticleCrm[] = [];
    articleCrm?: ArticleCrm;
    planningMaterials: ColoredMaterial[] = [];
    catalogMaterials: ColoredMaterial[] = [];
    catalogLoaded = false;
    catalogLoading = false;
    materialTab: 'planned' | 'unplanned' = 'planned';
    matchedMaterials: ColoredMaterial[] = [];
    optionalMaterials: OptionalMaterial[] = [];
    matchedOptionals: OptionalMaterial[] = [];
    materialTypes: MaterialType[] = [];
    rawMaterials: RawMaterial[] = [];
    groupedOptionals: GroupedOptional[] = [];
    selectedOptionalsMap: Map<number, RawMaterial | RawMaterial[]> = new Map();
    article!: Article;
    material!: ColoredMaterial;
    usedOptionalMaterials: OptionalMaterial[] = [];
    searching = false;
    selectedIndex = 0;
    location!: string;
    locations: string[] = this.data.locations;
    activeStep: STEPS = STEPS.LOCATION;
    STEPS = STEPS;

    @ViewChildren('groupItem')
    groupItems!: QueryList<ElementRef<HTMLElement>>;

    @ViewChildren('locationItem')
    locationItems!: QueryList<ElementRef<HTMLElement>>;

    @ViewChildren('articleItem')
    articleItems!: QueryList<ElementRef<HTMLElement>>;

    @ViewChildren('crmArticleItem')
    crmArticleItems!: QueryList<ElementRef<HTMLElement>>;

    @ViewChildren('materialItem')
    materialItems!: QueryList<ElementRef<HTMLElement>>;

    @ViewChild('selectedOptionals')
    selectedOptionals!: MatSelectionList;

    @ViewChild('stepFocusInput')
    stepFocusInput?: ElementRef<HTMLInputElement>;

    @ViewChild(MatRadioButton)
    optionalRadio?: MatRadioButton;

    private planningMaterials$: Observable<ColoredMaterial[]>;
    private stepHistory: STEPS[] = [STEPS.LOCATION];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { planningId: number; customerId?: number; locations: string[] },
        public dialogRef: MatDialogRef<AddMaterialDialogComponent>,
        private articlesService: ArticlesService,
        private materialService: MaterialService,
        private planningService: PlanningService
    ) {
        super();

        this.subscriptions.add(this.fcLocation.valueChanges.subscribe(() => (this.selectedIndex = 0)));

        if (this.data.customerId) {
            this.articlesService.getByCustomer(this.data.customerId).subscribe(crmArticles => {
                // Per standaardpost één regel (klantkopie, anders de standaardpost): de projectprijs volgt bij het afrekenen.
                const customerCopyIds = new Set(
                    crmArticles
                        .filter(crmArticle => crmArticle.generic_article_crm_id && !crmArticle.afas_project_id)
                        .map(crmArticle => crmArticle.generic_article_crm_id)
                );
                this.crmArticles = crmArticles.filter(
                    crmArticle =>
                        !crmArticle.blocked &&
                        !(crmArticle.generic_article_crm_id && crmArticle.afas_project_id) &&
                        !customerCopyIds.has(crmArticle.id)
                );
                this.matchedCrmArticles = this.crmArticles;
            });
        }

        this.articlesService.getList().subscribe(articles => {
            const grouped = new Map<string, Article[]>();

            for (const article of articles) {
                if (!grouped.has(article.group)) {
                    grouped.set(article.group, []);
                }

                grouped.get(article.group).push(article);
            }

            this.articles = Array.from(grouped, ([group, items]) => ({ group, items }));

            this.matchedGroups = this.articles;
        });

        this.planningMaterials$ = combineLatest([
            this.materialService.getForPlanning(this.data.planningId),
            this.planningService.materials$,
        ]).pipe(
            map(([pivots, catalog]) =>
                mergePlanningMaterialPivots(catalog, pivots).map(
                    m => ({ ...m, partOfPlanning: true }) as ColoredMaterial
                )
            ),
            shareReplay(1)
        );

        this.subscriptions.add(
            this.planningMaterials$.subscribe(planningMaterials => {
                this.planningMaterials = planningMaterials;

                if (this.materialTab === 'planned') {
                    this.applyMaterialFilter();
                }
            })
        );

        this.subscriptions.add(
            this.fcArticleSearch.valueChanges.pipe(debounceTime(200)).subscribe(search => {
                this.searching = true;

                const searchValue = (search ?? '').toString().trim();

                if (this.selectedGroupName) {
                    this.filterGroupArticles(searchValue);

                    this.searching = false;
                    this.selectedIndex = 0;

                    this.scrollToSelected(this.articleItems);

                    return;
                }

                if (!searchValue.length) {
                    this.matchedGroups = this.articles;
                } else {
                    this.matchedGroups = this.articles.flatMap(group => {
                        if (go(searchValue, [group], { key: 'group', threshold: -10000 }).length > 0) {
                            return [group];
                        }

                        return go(searchValue, group.items, { key: 'name', threshold: -10000 }).map(result => ({
                            group: group.group,
                            items: [result.obj],
                        }));
                    });
                }

                this.searching = false;
                this.selectedIndex = 0;

                this.scrollToSelected(this.groupItems);
            })
        );

        this.subscriptions.add(
            this.fcCrmSearch.valueChanges.pipe(debounceTime(200)).subscribe(search => {
                this.searching = true;

                const searchValue = (search ?? '').toString().trim();

                this.matchedCrmArticles = !searchValue.length
                    ? this.crmArticles
                    : go(searchValue, this.crmArticles, {
                          keys: ['crm_article_id', 'description'],
                          threshold: -10000,
                      }).map(r => r.obj);

                this.searching = false;
                this.selectedIndex = 0;

                this.scrollToSelected(this.crmArticleItems);
            })
        );

        this.subscriptions.add(
            this.fcMaterialSearch.valueChanges.pipe(debounceTime(200)).subscribe(search => {
                this.searching = true;

                this.applyMaterialFilter(search);

                this.searching = false;
                this.selectedIndex = 0;

                this.scrollToSelected(this.materialItems);
            })
        );
    }

    ngAfterViewInit() {
        this.focusStepInput();
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (event.key === 'Backspace') {
            const input = event.target instanceof HTMLInputElement ? event.target : null;

            if (input?.value) {
                return;
            }

            event.preventDefault();

            if (this.stepHistory.length > 1) {
                this.stepHistory.pop();
                this.activeStep = this.stepHistory.at(-1)!;
                this.focusStepInput();
            }

            return;
        }

        let listLength = 0;
        let items: QueryList<ElementRef<HTMLElement>> | undefined;

        switch (this.activeStep) {
            case STEPS.LOCATION:
                listLength = this.locationItems?.length ?? 0;
                items = this.locationItems;
                break;
            case STEPS.ARTICLE:
                if (this.pickerMode === 'bestekpost') {
                    listLength = this.matchedCrmArticles?.length ?? 0;
                    items = this.crmArticleItems;
                } else if (this.selectedGroupName) {
                    listLength = this.matchedGroupArticles?.length ?? 0;
                    items = this.articleItems;
                } else {
                    listLength = this.matchedGroups?.length ?? 0;
                    items = this.groupItems;
                }
                break;
            case STEPS.MATERIAL:
                listLength = this.matchedMaterials?.length ?? 0;
                items = this.materialItems;
                break;
        }

        if (event.key === 'Enter') {
            switch (this.activeStep) {
                case STEPS.LOCATION: {
                    const locations = [
                        ...(this.fcLocation.value?.length ? [this.fcLocation.value] : []),
                        ...this.locations,
                    ];
                    const location = locations[this.selectedIndex];

                    if (location) {
                        this.selectLocation(location);
                    }
                    break;
                }
                case STEPS.ARTICLE:
                    if (this.pickerMode === 'bestekpost') {
                        const crmArticle = this.matchedCrmArticles[this.selectedIndex];

                        if (crmArticle) {
                            this.selectBestekpost(crmArticle);
                        }
                    } else if (this.selectedGroupName) {
                        const article = this.matchedGroupArticles[this.selectedIndex];

                        if (article) {
                            this.selectArticle(article, this.selectedGroupName);
                        }
                    } else {
                        const group = this.matchedGroups[this.selectedIndex];

                        if (group) {
                            this.selectGroup(group.group, group.items.length === 1 ? group.items[0] : undefined);
                        }
                    }
                    break;
                case STEPS.MATERIAL:
                    this.selectMaterial(this.matchedMaterials[this.selectedIndex]);
                    break;
            }
        }

        if (event.key === 'ArrowUp') {
            if (listLength > 0) {
                this.selectedIndex = this.selectedIndex - 1;

                if (this.selectedIndex < 0) {
                    this.selectedIndex = listLength - 1;
                }

                this.scrollToSelected(items!);
            }
        }

        if (event.key === 'ArrowDown') {
            if (listLength > 0) {
                this.selectedIndex++;

                if (this.selectedIndex > listLength - 1) {
                    this.selectedIndex = 0;
                }

                this.scrollToSelected(items!);
            }
        }
    }

    selectMaterialTab(tab: 'planned' | 'unplanned') {
        if (this.materialTab === tab) {
            return;
        }

        this.materialTab = tab;
        this.selectedIndex = 0;
        this.focusStepInput();

        if (tab === 'unplanned' && !this.catalogLoaded) {
            this.loadCatalogMaterials();

            return;
        }

        this.applyMaterialFilter();
    }

    add() {
        if (!this.fcAmount.value || this.fcAmount.value <= 0) {
            return;
        }

        if (this.pickerMode === 'bestekpost') {
            this.dialogRef.close({
                articleCrm: this.articleCrm,
                amount: this.fcAmount.value,
                location: this.location,
            });

            return;
        }

        const optionalMaterialIds = new Set<number>(this.usedOptionalMaterials.map(m => m.id));

        this.material.raw_materials = this.material.raw_materials?.filter(
            raw => raw.material_type?.main || optionalMaterialIds.has(raw.id)
        );

        this.dialogRef.close({
            article: this.article,
            material: this.material,
            amount: this.fcAmount.value,
            location: this.location,
        });
    }

    private goToStep(step: STEPS) {
        if (this.stepHistory.at(-1) !== step) {
            this.stepHistory.push(step);
        }

        this.activeStep = step;
        this.focusStepInput();
    }

    private focusStepInput() {
        if (this.Utils.isMobile() && this.activeStep !== STEPS.AMOUNT) {
            return;
        }

        setTimeout(() =>
            this.stepFocusInput ? this.stepFocusInput.nativeElement.focus() : this.optionalRadio?.focus()
        );
    }

    setActiveStep(step: STEPS) {
        if (this.activeStep === STEPS.LOCATION) {
            this.selectLocation(this.fcLocation.value);

            return;
        }

        const restoreGroupSelection = step === STEPS.ARTICLE && !!this.selectedGroupName;

        if (restoreGroupSelection) {
            this.selectedGroupName = null;
            this.matchedGroups = this.articles;

            this.fcArticleSearch.setValue('', { emitEvent: false });
            this.searching = false;
        }

        this.goToStep(step);

        if (restoreGroupSelection) {
            this.selectedIndex = Math.max(
                0,
                this.matchedGroups.findIndex(g => g.group === this.article?.group)
            );

            // De ARTICLE-DOM (groepenlijst) wordt hier net (opnieuw) opgebouwd door de @if-wissel
            // hierboven; wacht een tick zodat groupItems het nieuwe element al bevat om naartoe te scrollen.
            setTimeout(() => this.scrollToSelected(this.groupItems));
        }
    }

    selectGroup(group: string, matchedArticle?: { id: number; name: string }) {
        if (matchedArticle) {
            this.selectArticle(matchedArticle, group);

            return;
        }

        const items = this.articles.find(g => g.group === group)?.items ?? [];

        this.selectedGroupName = group;
        this.matchedGroupArticles = items;

        this.fcArticleSearch.setValue('', { emitEvent: false });
        this.searching = false;
        this.selectedIndex = 0;

        if (items.length === 1) {
            this.selectArticle(items[0], group);

            return;
        }

        this.focusStepInput();
    }

    backToGroups() {
        this.selectedGroupName = null;
        this.matchedGroups = this.articles;

        this.fcArticleSearch.setValue('', { emitEvent: false });
        this.searching = false;
        this.selectedIndex = 0;

        this.focusStepInput();
    }

    selectArticle(article: Partial<Article>, group: string) {
        this.article = { ...article, group } as Article;

        this.searching = false;
        this.selectedIndex = 0;

        this.materialTab = this.planningMaterials.length > 0 ? 'planned' : 'unplanned';

        if (this.materialTab === 'unplanned' && !this.catalogLoaded) {
            this.loadCatalogMaterials();
        } else {
            this.applyMaterialFilter();
        }

        this.goToStep(STEPS.MATERIAL);
    }

    setPickerMode(mode: 'markering' | 'bestekpost') {
        if (this.pickerMode === mode) {
            return;
        }

        this.pickerMode = mode;
        this.article = undefined as unknown as Article;
        this.articleCrm = undefined;
        this.selectedGroupName = null;
        this.matchedGroups = this.articles;
        this.selectedIndex = 0;
        this.fcArticleSearch.setValue('', { emitEvent: false });
        this.fcCrmSearch.setValue('', { emitEvent: false });
        this.matchedCrmArticles = this.crmArticles;

        this.focusStepInput();
    }

    selectBestekpost(articleCrm: ArticleCrm) {
        if (!articleCrm.has_price) {
            return;
        }

        this.articleCrm = articleCrm;

        this.searching = false;
        this.selectedIndex = 0;
        this.usedOptionalMaterials = [];

        this.goToStep(STEPS.AMOUNT);
    }

    setOptionalMaterials(optionalMaterials: OptionalMaterial[]) {
        const hasOptionalMaterials = optionalMaterials.filter(m => m.optional).length > 0;

        if (hasOptionalMaterials) {
            this.usedOptionalMaterials = [];

            this.optionalMaterials = optionalMaterials;
            this.matchedOptionals = this.optionalMaterials;

            this.goToStep(STEPS.OPTIONAL);
        } else {
            this.usedOptionalMaterials = this.material.raw_materials?.filter(
                m => !m.material_type?.main && !m.material_type?.hidden
            ) as unknown as OptionalMaterial[];

            this.optionalMaterials = this.usedOptionalMaterials;
            this.matchedOptionals = this.usedOptionalMaterials;

            this.goToStep(STEPS.AMOUNT);
        }
    }

    selectMaterial(material: ColoredMaterial) {
        this.material = material;

        this.searching = false;
        this.selectedIndex = 0;

        if (material.partOfPlanning) {
            // Gepland materiaal: vast, dus geen vrije keuze - toon (en laat evt. optionele extra's
            // toggelen binnen) precies wat er al aan dit materiaal gekoppeld is.
            const planningOptionalMaterials = material.raw_materials?.filter(
                m => !m.material_type?.main && !m.material_type?.hidden
            ) as unknown as OptionalMaterial[];

            this.setOptionalMaterials(planningOptionalMaterials);

            return;
        }

        // Niet gepland: dezelfde keuze als bij het kiezen van een materiaal voor een planning -
        // gegroepeerd per (via material_types_material_types) gerelateerd materiaaltype, met de
        // mogelijkheid om nastrooi e.d. ook te kiezen als dit specifieke materiaal nog geen
        // gekoppelde optie van dat type heeft.
        const linkedMaterials = (material.raw_materials?.filter(
            (rm: RawMaterial & { pivot?: { optional?: boolean } }) => rm.material_type?.main === false
        ) ?? []) as RawMaterial[];

        if (!linkedMaterials.length) {
            this.usedOptionalMaterials = [];
            this.goToStep(STEPS.AMOUNT);

            return;
        }

        const mainRawMaterial = material.raw_materials?.find(rm => rm.material_type?.main === true);

        // Fris opvragen (i.p.v. een mogelijk nog lege component-property gebruiken): dit dialoog wordt
        // elke keer opnieuw aangemaakt, dus materialTypes/rawMaterials kunnen nog niet binnen zijn als
        // de gebruiker snel doorklikt naar dit punt. getMaterialTypes()/getAllRawMaterials() zijn
        // shareReplay(1), dus dit levert de al-gecachte waarde direct, of wacht netjes tot ze binnen zijn.
        this.subscriptions.add(
            combineLatest([
                this.materialService.getMaterialTypes(),
                this.materialService.getAllRawMaterials(),
            ]).subscribe(([materialTypes, rawMaterials]) => {
                this.materialTypes = materialTypes || [];
                this.rawMaterials = rawMaterials || [];

                const mainType = this.materialTypes.find(mt => mt.id === mainRawMaterial?.material_type_id);

                this.groupedOptionals = groupOptionalsByRelatedType(
                    linkedMaterials,
                    mainType,
                    this.materialTypes,
                    this.rawMaterials
                );

                if (!this.groupedOptionals.length) {
                    // Alles wat gekoppeld is, is verplicht: niets te kiezen, gewoon meenemen.
                    this.usedOptionalMaterials = linkedMaterials as unknown as OptionalMaterial[];
                    this.goToStep(STEPS.AMOUNT);

                    return;
                }

                this.selectedOptionalsMap = new Map();
                this.goToStep(STEPS.OPTIONAL);
            })
        );
    }

    selectOptional() {
        // Verplichte non-main grondstoffen altijd bevriezen, los van wat er is aangevinkt: de checkbox
        // staat hiervoor wel disabled+aangevinkt in de template, maar dat mag geen vereiste zijn om dit
        // hier al af te dwingen.
        const mandatory = this.optionalMaterials.filter(m => m.optional !== true);
        const selectedOptionalOnly = (
            this.selectedOptionals.selectedOptions.selected.map(option => option.value) as OptionalMaterial[]
        ).filter(m => m.optional === true);

        this.usedOptionalMaterials = [...mandatory, ...selectedOptionalOnly];

        this.goToStep(STEPS.AMOUNT);
    }

    isSelectedCheckbox(materialTypeId: number, raw: RawMaterial): boolean {
        const sel = this.selectedOptionalsMap.get(materialTypeId);

        return Array.isArray(sel) && sel.some(r => r.id === raw.id);
    }

    toggleCheckbox(materialTypeId: number, raw: RawMaterial) {
        const current = (this.selectedOptionalsMap.get(materialTypeId) as RawMaterial[]) || [];
        const idx = current.findIndex(r => r.id === raw.id);

        if (idx >= 0) {
            this.selectedOptionalsMap.set(
                materialTypeId,
                current.filter((_, i) => i !== idx)
            );
        } else {
            this.selectedOptionalsMap.set(materialTypeId, [...current, raw]);
        }
    }

    isOptionalSelectionValid(): boolean {
        return checkOptionalSelectionValid(this.groupedOptionals, this.selectedOptionalsMap);
    }

    selectGroupedOptionals() {
        const nonOptionalRawMaterials = (this.material.raw_materials?.filter(
            (rm: RawMaterial & { pivot?: { optional?: boolean } }) =>
                rm.material_type?.main === false && !rm.pivot?.optional
        ) ?? []) as unknown as OptionalMaterial[];

        const selectedFromMap = flattenSelectedOptionals(this.selectedOptionalsMap);

        this.usedOptionalMaterials = [
            ...nonOptionalRawMaterials,
            ...(selectedFromMap as unknown as OptionalMaterial[]),
        ];

        // Vrij gekozen grondstoffen die nog niet aan dit materiaal gekoppeld waren (uit de volledige
        // catalogus gekozen) moeten ook echt meekomen - add() filtert anders alleen bestaande raw_materials.
        const existingIds = new Set(this.material.raw_materials?.map(rm => rm.id));
        const newRawMaterials = selectedFromMap.filter(rm => !existingIds.has(rm.id));

        if (newRawMaterials.length) {
            this.material = {
                ...this.material,
                raw_materials: [...(this.material.raw_materials ?? []), ...newRawMaterials],
            };
        }

        this.goToStep(STEPS.AMOUNT);
    }

    selectLocation(location: string) {
        this.location = location;

        this.goToStep(STEPS.ARTICLE);
    }

    private loadCatalogMaterials() {
        this.catalogLoading = true;

        this.subscriptions.add(
            this.materialService.getList().subscribe(materials => {
                this.catalogMaterials = (materials as ColoredMaterial[]).map(m => ({ ...m, partOfPlanning: false }));
                this.catalogLoaded = true;
                this.catalogLoading = false;

                this.applyMaterialFilter();
            })
        );
    }

    private applyMaterialFilter(searchValue?: string) {
        const value = (searchValue ?? this.fcMaterialSearch.value ?? '').toString().trim();
        const source = this.materialTab === 'planned' ? this.planningMaterials : this.catalogMaterials;

        this.matchedMaterials = !value.length
            ? source
            : go(value, source, { keys: ['name', 'color_name', 'ral'], threshold: -10000 }).map(r => r.obj);
    }

    private filterGroupArticles(searchValue: string) {
        const groupArticles = this.articles.find(g => g.group === this.selectedGroupName)?.items ?? [];

        if (!searchValue.length) {
            this.matchedGroupArticles = groupArticles;

            return;
        }

        this.matchedGroupArticles = go(searchValue, groupArticles, { key: 'name', threshold: -10000 }).map(r => r.obj);
    }

    private scrollToSelected(items: QueryList<ElementRef<HTMLElement>>) {
        const target = items.get(this.selectedIndex)?.nativeElement;

        if (!target) {
            return;
        }

        setTimeout(() => {
            target.scrollIntoView({ block: 'nearest' });
        }, 0);
    }
}
